/**
 * /operations/attendance/shift-roster — Session 3B PR 1.
 *
 * Owning agent: Tashghil. AI surfaces: AiBriefPanel (right rail) +
 * InlineAISuggestion (unassigned-employees nudge) + TraceableValue
 * (active-period derivation + override-source).
 */

import { useQuery } from "@tanstack/react-query";
import { CalendarClock, Plus, UserPlus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, PageHeader, TraceableValue } from "../components/ui";
import { useLegalEntityGate } from "../lib/legalEntity";
import { session3bApi } from "../lib/session3b-api";
import type { ShiftRosterItem } from "../lib/session3b-api";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { showAlertTrace } from "./_session3aHelpers";

type StatusFilter = "all" | "active" | "unassigned";

function _statusToneClass(s: ShiftRosterItem["status"]): string {
  if (s === "unassigned") return "bg-rose-100 text-rose-800";
  if (s === "ended") return "bg-amber-100 text-amber-800";
  return "bg-emerald-100 text-emerald-800";
}

const _DOW_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

export function AttendanceShiftRosterPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const list = useQuery({
    queryKey: ["session3b-shift-roster", legalEntityId],
    queryFn: () => session3bApi.listShiftRoster(legalEntityId ?? "", 7),
    enabled: !!legalEntityId,
  });

  const items: ShiftRosterItem[] = list.data?.items ?? [];
  const filtered = useMemo(() => {
    if (statusFilter === "all") return items;
    return items.filter((r) => r.status === statusFilter);
  }, [items, statusFilter]);

  const counts = useMemo(() => {
    const unassigned = items.filter((r) => r.status === "unassigned").length;
    return {
      total: items.length,
      attention: unassigned,
      critical: 0,
      first_unassigned: items.find((r) => r.status === "unassigned"),
    };
  }, [items]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("attendance.shift_roster.title", "Shift Roster")}
          subtitle={t(
            "attendance.shift_roster.subtitle",
            "Who's scheduled to which shift. Tashghil tracks coverage and flags unassigned employees.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {(["all", "active", "unassigned"] as StatusFilter[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setStatusFilter(f)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  statusFilter === f
                    ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {t(`attendance.shift_roster.filter.${f}`, f)}
                {f === "unassigned" && counts.attention > 0 && (
                  <span className="ms-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-600 px-1 text-[9px] font-semibold text-white">
                    {counts.attention}
                  </span>
                )}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "attendance.shift_roster.cta.assign_note",
                  "Assign Shift drawer: pick employees + shift + date range. The drawer ships in the M3X.SHIFTS pack; today the surface accepts the inputs and queues them for the wiring sprint.",
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <Plus className="h-4 w-4" />
            {t("attendance.shift_roster.cta.assign", "Assign Shift")}
          </button>
        </div>

        {counts.first_unassigned && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<UserPlus className="h-4 w-4" />}
            severity="warning"
            headline={t(
              "attendance.shift_roster.suggestion.unassigned_headline",
              `${counts.attention} ${counts.attention === 1 ? "employee has" : "employees have"} no current shift assigned.`,
            )}
            body={t(
              "attendance.shift_roster.suggestion.unassigned_body",
              `Tashghil suggests assigning the default shift to ${counts.first_unassigned.employee_name_en}.`,
            )}
            primaryAction={{
              label: t(
                "attendance.shift_roster.suggestion.assign_default",
                "Assign default shift",
              ),
              onClick: () =>
                showAlertTrace(
                  t(
                    "attendance.shift_roster.suggestion.assign_note",
                    "Bulk default-assign drawer ships in the M3X.SHIFTS pack. Today HR can use the manual + Assign Shift CTA above to assign one at a time.",
                  ),
                ),
            }}
            suggestionKey={`shift-unassigned-${counts.first_unassigned.employee_id}`}
          />
        )}
        {counts.total === 0 && !list.isLoading && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<CalendarClock className="h-4 w-4" />}
            severity="info"
            headline={t(
              "attendance.shift_roster.suggestion.empty_headline",
              "No employees in scope for this LE.",
            )}
            body={t(
              "attendance.shift_roster.suggestion.empty_body",
              "Tashghil notes: hire someone first via the Add Employee wizard, then come back here to assign their shift.",
            )}
            primaryAction={{
              label: t("attendance.shift_roster.suggestion.add_employee", "Add an employee"),
              onClick: () => window.location.assign("/people/employees/new"),
            }}
            suggestionKey="shift-empty"
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {filtered.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("attendance.shift_roster.col.employee", "Employee")}</th>
                  <th className="px-3 py-2">{t("attendance.shift_roster.col.shift", "Shift")}</th>
                  <th className="px-3 py-2">{t("attendance.shift_roster.col.days", "Days")}</th>
                  <th className="px-3 py-2">{t("attendance.shift_roster.col.period", "Active period")}</th>
                  <th className="px-3 py-2">{t("attendance.shift_roster.col.source", "Source")}</th>
                  <th className="px-3 py-2">{t("attendance.shift_roster.col.status", "Status")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr
                    key={row.employee_id}
                    className="group border-t border-border-subtle hover:bg-surface-sunken/40"
                  >
                    <td className="px-3 py-2">
                      <Link
                        to={`/people/employees/${row.employee_id}`}
                        className="font-medium text-[#2260C9] hover:underline"
                      >
                        {row.employee_name_en}
                      </Link>
                      <div className="text-xs text-text-tertiary">{row.employee_code}</div>
                    </td>
                    <td className="px-3 py-2">
                      {row.shift_code ? (
                        <Link
                          to="/policies/shifts"
                          className="text-[#2260C9] hover:underline"
                        >
                          {row.shift_name_en ?? row.shift_code}
                        </Link>
                      ) : (
                        <span className="text-text-tertiary">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      {row.days_of_week.length === 0
                        ? "—"
                        : row.days_of_week.map((i) => _DOW_LABELS[i]).join(" · ")}
                    </td>
                    <td className="px-3 py-2 text-text-secondary">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "attendance.shift_roster.trace.period",
                              `Active period = shift_assignments.effective_from (${row.active_from}) → effective_to (${row.active_to ?? "open"}). Open-ended assignments stay current until explicitly closed.`,
                            ),
                          )
                        }
                      >
                        <span dir="ltr">
                          {row.active_from} → {row.active_to ?? "—"}
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "attendance.shift_roster.trace.source",
                              "Source = 'custom' when the assignment came from an explicit ShiftAssignment row; 'default' otherwise (shift inherited from position's default catalog mapping).",
                            ),
                          )
                        }
                      >
                        <span className="text-xs text-text-secondary">
                          {row.override_source}
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${_statusToneClass(row.status)}`}
                      >
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!list.isLoading && filtered.length === 0 && items.length > 0 && (
          <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
            {t(
              "attendance.shift_roster.no_match",
              "No employees match the current filter. Clear the filter to see the full roster.",
            )}
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="tashghil"
          liveContext={{
            total: counts.total,
            attention: counts.attention,
            critical: 0,
            top_kind: t("attendance.shift_roster.title", "Shift Roster"),
            window_label: t("attendance.shift_roster.window_label", "this week"),
          }}
        />
      </aside>
    </div>
  );
}
